/**
 * Filters Screen
 *
 * Lets users define filters that narrow the master dataset before analysis.
 * Two filter types are supported:
 *   - Binary filters: include/exclude specific discrete values in a column.
 *   - Regex filters: keep rows whose column value matches a regular expression.
 *
 * Filters are saved to the project and applied server-side via the Python backend.
 * The "Apply all Filters" button re-generates `filtered.parquet.gzip` from
 * `master.parquet.gzip` with all active filters.
 */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  getFilterValues,
  getFilterStats,
  removeFilter,
  addValue,
  removeValue,
  addFilter,
  applyAllFilters,
  includeAll,
  excludeAll,
  invertFilter,
} from "Redux/filter/actions";
import {
  addRegexFilter,
  removeRegexFilter,
  updateRegexPattern,
} from "Redux/regexfilter/actions";

// -------------------------------------------------------------
// Combined Filters Screen
// -------------------------------------------------------------
const Filters = () => {
  const dispatch = useDispatch();
  const project = useSelector((state) => state.project);
  const filters = project.filters || [];
  const regexFilters = project.regexFilters || [];
  const filtersHaveChanged = project.filtersHaveChanged;

  const [applying, setApplying] = useState(false);
  const [masterSize, setMasterSize] = useState(null);
  const [filteredSize, setFilteredSize] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      const stats = await getFilterStats();
      if (stats) {
        setMasterSize(stats.masterSize);
        setFilteredSize(stats.filteredSize);
      }
    };
    fetchStats();
  }, []);

  const mappedSet = new Set();
  const includedCollections = project.collections.filter((collection) =>
    project.included.includes(collection.id)
  );
  includedCollections.forEach((collection) => {
    const values = Object.values(collection.mapping);
    for (const value of values) {
      mappedSet.add(value);
    }
  });
  const mappedColumns = Array.from(mappedSet);

  const buttonClass = filtersHaveChanged ? "is-warning" : "";

  return (
    <div>
      <div className="level">
        <div className="level-left">
          <div className="level-item">
            <div
              className={`button is-small ${applying ? "is-loading" : ""} ${buttonClass}`}
              disabled={!filtersHaveChanged}
              onClick={() => {
                setApplying(true);
                dispatch(
                  applyAllFilters((data) => {
                    setApplying(false);
                    if (data) {
                      setMasterSize(data.masterSize);
                      setFilteredSize(data.filteredSize);
                    }
                  })
                );
              }}
            >
              <span className="icon is-small">
                <i className="fa-duotone fa-filters" />
              </span>
              <span>Apply all Filters</span>
            </div>
          </div>
          {masterSize !== null && filteredSize !== null && (
            <div className="level-item">
              <span className="tag is-light is-medium">
                Master: <strong className="ml-1">{masterSize.toLocaleString()}</strong>
              </span>
              <span className="tag is-light is-medium ml-2">
                Filtered: <strong className="ml-1">{filteredSize.toLocaleString()}</strong>
              </span>
            </div>
          )}
        </div>
      </div>

      <BinaryFilterSection
        mappedColumns={mappedColumns}
        filters={filters}
      />

      <hr />

      <RegexFilterSection
        mappedColumns={mappedColumns}
        regexFilters={regexFilters}
      />
    </div>
  );
};

export default Filters;

// -------------------------------------------------------------
// Binary Filter Section
// Displays available mapped columns as clickable tags on the left.
// Clicking a column fetches its unique values from the backend and
// creates a new binary filter. Active filters appear as tabs on the right.
// -------------------------------------------------------------
const BinaryFilterSection = ({ mappedColumns, filters }) => {
  const dispatch = useDispatch();
  const [currentFilterId, setCurrentFilterId] = useState(0);
  const [adding, setAdding] = useState(null);

  const addColumn = async (column) => {
    setAdding(column);
    const { all, included, excluded } = await getFilterValues(column);
    const currentLength = filters.length;
    await dispatch(
      addFilter({
        index: currentLength,
        column,
        all,
        included,
        excluded,
      })
    );
    setCurrentFilterId(currentLength);
    setAdding(null);
  };

  const filteredColumns = filters.map((filter) => filter.column);

  return (
    <div className="box">
      <h4 className="title is-5">Binary Filters</h4>
      <div className="columns">
        <div className="column is-one-third">
          <div className="tags are-small">
            {mappedColumns.map((col) => {
              if (filteredColumns.includes(col))
                return (
                  <div key={col} className="tag is-taken">
                    <span>{col}</span>
                  </div>
                );
              if (adding === col) {
                return (
                  <div key={col} className="tag is-clickable">
                    <span>{col}</span>
                    <span className="icon is-small">
                      <i className="fa-duotone fa-cog fa-spin" />
                    </span>
                  </div>
                );
              }
              return (
                <div
                  key={col}
                  className="tag is-clickable"
                  onClick={() => addColumn(col)}
                >
                  <span>{col}</span>
                  <span className="icon is-small">&nbsp;</span>
                </div>
              );
            })}
          </div>
        </div>
        <div className="column">
          <div className="tabs is-small is-boxed">
            <ul>
              {filters.map((filter, index) => {
                return (
                  <li
                    key={index}
                    className={index === currentFilterId ? "is-active" : ""}
                  >
                    <a role="button" tabIndex={0} onClick={() => setCurrentFilterId(index)}>
                      {filter.column}
                      <span
                        className="icon is-small ml-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          dispatch(removeFilter(filter.column));
                          setCurrentFilterId(0);
                        }}
                      >
                        <i className="fa-solid fa-times" />
                      </span>
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
          <BinaryFilterEditor filter={filters[currentFilterId]} />
        </div>
      </div>
    </div>
  );
};

// -------------------------------------------------------------
// Binary Filter Editor
// Two-column view: clicking an included value moves it to excluded
// (and vice-versa), toggling whether rows with that value are kept.
// -------------------------------------------------------------
const BinaryFilterEditor = ({ filter }) => {
  const dispatch = useDispatch();

  if (!filter) return null;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "center", marginBottom: "0.5rem" }}>
        <button
          className="button is-small"
          onClick={() => dispatch(invertFilter(filter.column))}
          title="Swap included and excluded"
          style={{ borderRadius: "6px" }}
        >
          <span className="icon is-small">
            <i className="fa-duotone fa-right-left" />
          </span>
          <span>Invert</span>
        </button>
      </div>

      <div className="columns">
        <div className="column">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.3rem" }}>
            <span>Included Values</span>
            <button
              className="button is-small"
              onClick={() => dispatch(includeAll(filter.column))}
              style={{ borderRadius: "5px" }}
            >
              <span className="icon is-small">
                <i className="fa-duotone fa-check-double" />
              </span>
              <span>Include All</span>
            </button>
          </div>
          <div className="box">
            <div className="tags">
              {filter.included.map((value) => (
                <div
                  key={value}
                  className="tag is-clickable"
                  onClick={() => dispatch(removeValue(filter.column, value))}
                >
                  {value}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="column">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.3rem" }}>
            <span>Excluded</span>
            <button
              className="button is-small"
              onClick={() => dispatch(excludeAll(filter.column))}
              style={{ borderRadius: "5px" }}
            >
              <span className="icon is-small">
                <i className="fa-duotone fa-ban" />
              </span>
              <span>Exclude All</span>
            </button>
          </div>
          <div className="box">
            <div className="tags">
              {filter.excluded.map((value) => (
                <div
                  key={value}
                  className="tag is-clickable"
                  onClick={() => dispatch(addValue(filter.column, value))}
                >
                  {value}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// -------------------------------------------------------------
// Regex Filter Section
// -------------------------------------------------------------
const RegexFilterSection = ({ mappedColumns, regexFilters }) => {
  const dispatch = useDispatch();
  const [currentFilterId, setCurrentFilterId] = useState(0);

  const addColumn = (column) => {
    const currentLength = regexFilters.length;
    dispatch(
      addRegexFilter({
        column,
        pattern: "",
      })
    );
    setCurrentFilterId(currentLength);
  };

  const filteredColumns = regexFilters.map((filter) => filter.column);

  return (
    <div className="box">
      <h4 className="title is-5">Regex Filters</h4>
      <div className="columns">
        <div className="column is-one-third">
          <div className="tags are-small">
            {mappedColumns.map((col) => {
              if (filteredColumns.includes(col))
                return (
                  <div key={col} className="tag is-taken">
                    <span>{col}</span>
                  </div>
                );
              return (
                <div
                  key={col}
                  className="tag is-clickable"
                  onClick={() => addColumn(col)}
                >
                  <span>{col}</span>
                  <span className="icon is-small">&nbsp;</span>
                </div>
              );
            })}
          </div>
        </div>
        <div className="column">
          <div className="tabs is-small is-boxed">
            <ul>
              {regexFilters.map((filter, index) => {
                return (
                  <li
                    key={index}
                    className={index === currentFilterId ? "is-active" : ""}
                  >
                    <a role="button" tabIndex={0} onClick={() => setCurrentFilterId(index)}>
                      {filter.column}
                      <span
                        className="icon is-small ml-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          dispatch(removeRegexFilter(filter.column));
                          setCurrentFilterId(0);
                        }}
                      >
                        <i className="fa-solid fa-times" />
                      </span>
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
          <RegexFilterEditor filter={regexFilters[currentFilterId]} />
        </div>
      </div>
    </div>
  );
};

// -------------------------------------------------------------
// Regex Filter Editor
// Free-text regex input. The pattern is validated client-side
// (via `new RegExp`) before dispatching to Redux.
// -------------------------------------------------------------
const RegexFilterEditor = ({ filter }) => {
  const dispatch = useDispatch();
  const [localPattern, setLocalPattern] = useState(filter?.pattern || "");
  const [isValid, setIsValid] = useState(true);

  useEffect(() => {
    setLocalPattern(filter?.pattern || "");
    setIsValid(true);
  }, [filter?.column, filter?.pattern]);

  if (!filter) return null;

  const validateAndUpdate = (value) => {
    setLocalPattern(value);
    try {
      if (value) {
        new RegExp(value);
      }
      setIsValid(true);
      dispatch(updateRegexPattern(filter.column, value));
    } catch (e) {
      setIsValid(false);
    }
  };

  return (
    <>
      <div className="field">
        <label className="label is-small">Regular Expression Pattern</label>
        <div className="control has-icons-left">
          <input
            className={`input ${isValid ? "" : "is-danger"}`}
            type="text"
            placeholder="Enter regex pattern (e.g., ^ABC.*)"
            value={localPattern}
            onChange={(e) => validateAndUpdate(e.target.value)}
          />
          <span className="icon is-small is-left">
            <i className="fa-duotone fa-brackets-curly" />
          </span>
        </div>
        {!isValid && (
          <p className="help is-danger">Invalid regular expression</p>
        )}
      </div>

      <div className="box">
        <p className="is-size-7 has-text-grey">
          Rows where <strong>{filter.column}</strong> matches this pattern will be included.
        </p>
        {filter.pattern && isValid && (
          <p className="is-size-7 mt-2">
            <code>/{filter.pattern}/</code>
          </p>
        )}
      </div>
    </>
  );
};
